import {
  CreateOutreachProspectSchema,
  OUTREACH_STAGE_LABELS,
  OutreachStageSchema,
  type CreateOutreachProspectDto,
  type OutreachStage,
} from "@lumika/shared";

// Pure paste-import parsing — no React/UI deps so it's unit-testable.
// Turns pasted CSV/TSV (or a bare one-name-per-line list) into validated
// prospect payloads, skipping rows with no company or a bad email.

type Field =
  | "companyName"
  | "websiteUrl"
  | "linkedinUrl"
  | "contactName"
  | "contactTitle"
  | "email"
  | "phone"
  | "city"
  | "state"
  | "source"
  | "stage"
  | "demoAt"
  | "nextFollowUp"
  | "tags";

// Header synonyms → field. Headers are normalized (lowercased, stripped of
// non-alphanumerics) before lookup, so "Company Name" and "company_name"
// both match.
const HEADER_MAP: Record<string, Field> = {
  company: "companyName",
  companyname: "companyName",
  dealer: "companyName",
  dealership: "companyName",
  name: "companyName",
  website: "websiteUrl",
  url: "websiteUrl",
  site: "websiteUrl",
  linkedin: "linkedinUrl",
  linkedinurl: "linkedinUrl",
  linkedinprofile: "linkedinUrl",
  li: "linkedinUrl",
  contact: "contactName",
  contactname: "contactName",
  title: "contactTitle",
  contacttitle: "contactTitle",
  role: "contactTitle",
  email: "email",
  phone: "phone",
  tel: "phone",
  mobile: "phone",
  city: "city",
  state: "state",
  province: "state",
  source: "source",
  stage: "stage",
  status: "stage",
  demo: "demoAt",
  demoat: "demoAt",
  demodate: "demoAt",
  demotime: "demoAt",
  demoscheduled: "demoAt",
  followup: "nextFollowUp",
  nextfollowup: "nextFollowUp",
  recontact: "nextFollowUp",
  tags: "tags",
  tag: "tags",
  labels: "tags",
};

// Positional fallback when the paste has no recognizable header row.
const POSITIONAL: Field[] = [
  "companyName",
  "contactName",
  "email",
  "phone",
  "city",
  "state",
  "source",
];

const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, "");
const nz = (s?: string): string | null => (s && s.trim() ? s.trim() : null);

// Normalized lookup of every stage value AND its display label → the stored
// enum value, so "Demo scheduled", "demo_scheduled" and "demoscheduled" all
// resolve to `demo_scheduled` on import.
const STAGE_BY_NORM = new Map<string, OutreachStage>();
for (const stage of OutreachStageSchema.options) {
  STAGE_BY_NORM.set(norm(stage), stage);
  STAGE_BY_NORM.set(norm(OUTREACH_STAGE_LABELS[stage]), stage);
}

/**
 * Split one delimited line, honoring double-quoted fields so a delimiter
 * inside quotes (`"Smith, Inc.",jane@…`) doesn't break the column. `""`
 * inside a quoted field is an escaped quote. (Single-line only — a quoted
 * field spanning newlines isn't supported for paste import.)
 */
function splitDelimited(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line.charAt(i);
    if (inQuotes) {
      if (ch === '"' && line.charAt(i + 1) === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

function parseDateCell(raw: string | undefined): string | null {
  const s = (raw ?? "").trim();
  // Accept only an unambiguous ISO YYYY-MM-DD. Locale strings ("7/1/2026",
  // "July 1 2026") are timezone/format-ambiguous and `new Date()` parses
  // them as LOCAL midnight, day-shifting the stored UTC date — so leave the
  // cell blank instead and let the user set it in the date picker.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  // Stored at 00:00:00Z so the date round-trips with the date input.
  return new Date(`${s}T00:00:00Z`).toISOString();
}

function parseDateTimeCell(raw: string | undefined): string | null {
  const s = (raw ?? "").trim();
  if (!s) return null;
  // A bare date → midnight UTC, consistent with the follow-up rule.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00Z`).toISOString();
  // Otherwise require a full ISO 8601 instant with an explicit timezone (Z or
  // ±HH:MM). A bare local datetime is rejected — `new Date()` would read it as
  // local time and shift the stored UTC instant.
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function parseTags(raw: string | undefined): string[] {
  const s = (raw ?? "").trim();
  if (!s) return [];
  // Tags share a cell — split on ; , | so a comma-free TSV cell and a quoted
  // CSV cell both work. Drop empties and anything over the schema's 40-char
  // limit (so one stray long tag doesn't make the whole row fail to import),
  // and cap at 20 to match the prospect schema.
  return s
    .split(/[;,|]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0 && t.length <= 40)
    .slice(0, 20);
}

function validStage(raw: string | undefined): OutreachStage {
  return STAGE_BY_NORM.get(norm(raw ?? "")) ?? "new";
}

export type ParsedImport = {
  rows: CreateOutreachProspectDto[];
  skipped: number;
};

export function parseProspectRows(text: string): ParsedImport {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const [firstLine] = lines;
  if (!firstLine) return { rows: [], skipped: 0 };

  const delimiter = lines.some((l) => l.includes("\t")) ? "\t" : ",";
  const split = (line: string): string[] => splitDelimited(line, delimiter);

  const firstCells = split(firstLine);
  const headerFields = firstCells.map((c) => HEADER_MAP[norm(c)]);
  const hasHeader = headerFields.some(Boolean);

  const dataLines = hasHeader ? lines.slice(1) : lines;
  const columns: (Field | undefined)[] = hasHeader
    ? headerFields
    : firstCells.length === 1
      ? ["companyName"]
      : POSITIONAL;

  const rows: ParsedImport["rows"] = [];
  let skipped = 0;
  for (const line of dataLines) {
    const cells = split(line);
    const record: Partial<Record<Field, string>> = {};
    columns.forEach((field, i) => {
      const value = cells[i];
      if (field && value !== undefined) record[field] = value;
    });
    if (!record.companyName?.trim()) {
      skipped += 1;
      continue;
    }
    try {
      rows.push(
        CreateOutreachProspectSchema.parse({
          companyName: record.companyName.trim(),
          websiteUrl: nz(record.websiteUrl),
          linkedinUrl: nz(record.linkedinUrl),
          contactName: nz(record.contactName),
          contactTitle: nz(record.contactTitle),
          email: nz(record.email),
          phone: nz(record.phone),
          city: nz(record.city),
          state: nz(record.state),
          source: nz(record.source),
          stage: validStage(record.stage),
          ownerAdminId: null,
          demoAt: parseDateTimeCell(record.demoAt),
          nextFollowUpAt: parseDateCell(record.nextFollowUp),
          tags: parseTags(record.tags),
        }),
      );
    } catch {
      // Bad email / oversized value — skip the row, keep the batch.
      skipped += 1;
    }
  }
  return { rows, skipped };
}

// ─── Import schema (copy → hand to an AI to collect prospects) ────────────────

const SCHEMA_COLUMNS: { header: string; required?: boolean; note: string }[] = [
  { header: "company", required: true, note: "dealership / business name" },
  { header: "contactName", note: "primary contact's full name" },
  { header: "contactTitle", note: "their role — Owner, GM, …" },
  { header: "email", note: "contact email" },
  { header: "phone", note: "contact phone" },
  { header: "website", note: "company website URL" },
  { header: "linkedin", note: "LinkedIn URL (company or person)" },
  { header: "city", note: "city" },
  { header: "state", note: "state / province" },
  { header: "source", note: "how we found them — referral, cold, event, inbound, …" },
  { header: "stage", note: `one of: ${OutreachStageSchema.options.join(", ")} (default: new)` },
  {
    header: "demoAt",
    note: "scheduled demo as ISO 8601 UTC, e.g. 2026-07-01T15:00:00Z (or a plain date 2026-07-01)",
  },
  { header: "followUp", note: "next follow-up date, YYYY-MM-DD" },
  { header: "tags", note: "semicolon-separated, e.g. vip;warm-intro" },
];

const SCHEMA_EXAMPLE = [
  "Sunshine Auto Group",
  "John Carter",
  "GM",
  "john@sunshineauto.com",
  "+1 555-123-4567",
  "sunshineauto.com",
  "linkedin.com/company/sunshine-auto",
  "Austin",
  "TX",
  "referral",
  "interested",
  "2026-07-01T15:00:00Z",
  "2026-07-08",
  "vip;warm-intro",
].join(",");

/**
 * A self-contained spec an admin can copy and paste into an AI assistant so it
 * returns prospects in exactly the shape `parseProspectRows` accepts. Derived
 * from the canonical column list and live stage vocabulary so it never drifts.
 */
export const IMPORT_SCHEMA_TEXT = [
  "Lumika Outreach — prospect import schema",
  "",
  "Return ONLY CSV. The first line is the header below; one prospect per line after it.",
  "Only `company` is required — leave any unknown value blank. Wrap any value containing a comma in double quotes.",
  "",
  SCHEMA_COLUMNS.map((c) => c.header).join(","),
  "",
  "Fields:",
  ...SCHEMA_COLUMNS.map(
    (c) => `- ${c.header}${c.required ? " (required)" : ""} — ${c.note}`,
  ),
  "",
  "Example:",
  SCHEMA_EXAMPLE,
].join("\n");
